import React from 'react';
import { Play, Pause, Flag, CornerUpLeft } from 'lucide-react';
import { parseTrackTitle } from '@meradio/music-utils';
import { Button, IconButton } from '@/components/ui';
import { brand } from '@/theme/colors';
import { useDecks } from '@/features/player/PlayerProvider';
import type { DeckId } from '@/features/player/decksSlice';
import { featuresOf, metaBpm, metaKey, metaCamelot, formatTime, type DeckRowVideo } from '../trackMeta';

interface DeckPaneProps {
  deckId: DeckId;
}

const DeckPane: React.FC<DeckPaneProps> = ({ deckId }) => {
  const { decks, deckProgress, togglePlay, setCue, jumpToCue, seekDeck } = useDecks();

  const deck = decks[deckId];
  const progress = deckProgress[deckId];
  const video = deck.video;
  const isPlaying = deck.isPlaying;
  const parsed = video ? parseTrackTitle(video.title) : null;
  const features = featuresOf(video as DeckRowVideo | null);
  const label = deckId.toUpperCase();
  const percent = progress.duration > 0 ? Math.min(100, (progress.current / progress.duration) * 100) : 0;

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    if (progress.duration <= 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    seekDeck(deckId, fraction * progress.duration);
  };

  return (
    <section
      aria-label={`Deck ${label}`}
      className="flex flex-col gap-4 bg-white dark:bg-bg-dark-secondary rounded-xl border border-meradio-light dark:border-border-dark p-4"
    >
      <span className="text-xs font-semibold uppercase tracking-widest text-meradio-primary">
        Deck {label}
      </span>

      <div
        id={`deck-${deckId}-video-slot`}
        className="relative w-3/5 mx-auto aspect-video rounded-lg overflow-hidden bg-black"
      />

      <div className="flex items-center gap-4">
        <div
          data-testid={`deck-${deckId}-vinyl`}
          className={`relative w-16 h-16 flex-shrink-0 rounded-full ${isPlaying ? 'animate-spin-slow' : ''}`}
          style={{
            background: `radial-gradient(circle at 30% 30%, ${brand.dark} 0%, ${brand.darkLight} 40%, ${brand.dark} 70%, #000 100%)`,
          }}
        >
          <div className="absolute inset-1 rounded-full border border-neutral-600 opacity-30" />
          <div className="absolute inset-2 rounded-full border border-neutral-600 opacity-20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-meradio-primary rounded-full flex items-center justify-center shadow-inner">
            <div className="w-1.5 h-1.5 bg-meradio-dark rounded-full" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-lg font-bold text-meradio-dark dark:text-text-dark-primary truncate">
            {parsed ? parsed.track : 'No track loaded'}
          </p>
          <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary truncate">
            {parsed?.artist ?? '—'}
          </p>
        </div>
      </div>

      <div className="flex items-baseline gap-3 text-2xl font-bold tabular-nums text-meradio-dark dark:text-text-dark-primary">
        <span>{metaBpm(features)}</span>
        <span className="text-meradio-dark-light dark:text-text-dark-tertiary">·</span>
        <span>{metaKey(features)}</span>
        <span className="text-meradio-dark-light dark:text-text-dark-tertiary">·</span>
        <span className="text-meradio-primary">{metaCamelot(features)}</span>
      </div>

      <div className="space-y-1">
        <div
          role="slider"
          tabIndex={0}
          aria-label={`Seek deck ${label}`}
          aria-valuemin={0}
          aria-valuemax={Math.round(progress.duration)}
          aria-valuenow={Math.round(progress.current)}
          onClick={handleSeek}
          className="h-2 rounded-full bg-meradio-light dark:bg-bg-dark-tertiary overflow-hidden cursor-pointer"
        >
          <div className="h-full bg-meradio-primary" style={{ width: `${percent}%` }} />
        </div>
        <div className="flex justify-between text-xs tabular-nums text-meradio-dark-light dark:text-text-dark-secondary">
          <span>{formatTime(progress.current)}</span>
          <span>{formatTime(progress.duration)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <IconButton
          onClick={() => togglePlay(deckId)}
          variant="solid"
          size="lg"
          aria-label={isPlaying ? `Pause deck ${label}` : `Play deck ${label}`}
        >
          {isPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
        </IconButton>
        <Button variant="outline" size="sm" onClick={() => setCue(deckId)}>
          <Flag className="w-4 h-4" />
          Set cue
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => jumpToCue(deckId)}
          disabled={deck.cuePoint == null}
        >
          <CornerUpLeft className="w-4 h-4" />
          Cue
        </Button>
      </div>
    </section>
  );
};

export default DeckPane;
